import { mat4, vec3 } from 'gl-matrix';
import { Context } from '../context/Context';
import { Camera } from '../camera';
import { BoundingDimensions } from '../util';
import { PropertyAnimation } from '../animation';
import { ObjectInteraction } from '../object';
import { Light } from '../light';
import { RenderTexture } from './RenderTarget';
import { LightDataTexture } from '../texture/LightDataTexture';
import { TileDataTexture } from '../texture/TileDataTexture';

export * from './SceneRenderer';
export * from './PickingSceneRenderer';
export * from './DepthMapRenderer';
export * from './CompositeSceneRenderer';
export * from './DeferredSceneRenderer';
export * from './GBuffer';
export * from './FullscreenQuad';
export * from './RenderTarget';
export * from './Decal';
export * from './InstanceGroup';
export * from './InstanceManager';
export * from './GraphicsSettings';
export * from './HistoryBuffer';
export * from './TaaPass';

export enum RenderMode {
  Primary,
  Picking,
  Occlusion,
  Depth,
  GBuffer,
  Transparent,
  Overlay,
}

export enum DepthMode {
  CameraDistance = 0,
  PointDistance = 1,
}

export type RenderSettings = {
  mode: RenderMode;
  objects: DrawableObject[];
  lights: Light[];
  lightDataTexture?: LightDataTexture;
  tileDataTexture?: TileDataTexture;
  tilesX?: number;
  tileSize?: number;
  volumetricTexture?: RenderTexture;
  sceneDepthTexture?: RenderTexture;
  invViewProjection?: mat4;
  screenSize?: [number, number];
  pickingReference?: number;
  depthMode?: DepthMode;
  depthPoint?: vec3 | number[];
  /** When true, the gbuffer shader writes 1.0 into albedo.a (coverage) instead of metallic. Used by ImpostorTextureGenerator. */
  impostorOutput?: boolean;
  /** [localMin.y, localMax.y] of the captured mesh — drives height-based AO darkening during impostor capture. */
  impostorHeightBounds?: [number, number];
};

export interface DrawableObject {
  bounds?: BoundingDimensions;
  animatedProperties?: PropertyAnimation;
  interaction?: ObjectInteraction;

  renderConfiguration: RenderConfiguration;

  render(ctx: Context, camera: Camera, settings: RenderSettings): void;

  getShaderSortKey?(mode: RenderMode): number;

  /**
   * Optional cleanup hook called by ObjectManager.clear() when the scene is
   * detached. Implementers should release any GL resources they own (textures,
   * VAOs, buffers) here so they don't leak across scene transitions.
   */
  destroy?(): void;
}

export type RenderConfiguration = {
  culling?:
    | 'none' // No culling will be performed, object will always be rendered. Appropriate for a skybox, UI elements, and complex/large objects that are always visible (e.g. floors).
    | 'list' // The default, objects are kept in a list and checked against the frustum for visibility.
    | 'octree'; // When the list of objects is really large and it consists mostly of static objects somewhat uniformly spread then an octree might perform better than a list.
  lightning?: ('static' | 'dynamic' | 'baked' | 'lightmap')[];
  shadow?: 'none' | 'cast' | 'recieve' | 'cast-recieve';
  static?: boolean; // Indicates that the object will never be moved.
  order?: number; // Helps determine the draw order of objects. Lower number are drawn first.
  modes?: RenderMode[]; // Defaults to all modes.
};
